/**
 * `<PriceChart>` — the ito wrapper around TradingView's lightweight-charts.
 *
 * Fully token-driven (follows the active `data-theme` automatically) and
 * composed with series children:
 *
 * @example
 * <PriceChart className="h-96">
 *   <CandlestickSeries data={candles} />
 *   <HistogramSeries data={volume} paneIndex={1} options={{ priceFormat: { type: "volume" } }} />
 *   <LineSeries data={sma}>
 *     <PriceLine price={100} title="Avg" />
 *   </LineSeries>
 * </PriceChart>
 *
 * Every option remains overridable via `options` (raw lightweight-charts
 * ChartOptions) and per-series `options`. Imperative access via
 * `usePriceChart()` / the `chartApiRef` prop.
 */

import * as React from "react";
import {
  createChart,
  createSeriesMarkers,
  createTextWatermark,
  AreaSeries as AreaSeriesDef,
  BarSeries as BarSeriesDef,
  BaselineSeries as BaselineSeriesDef,
  CandlestickSeries as CandlestickSeriesDef,
  HistogramSeries as HistogramSeriesDef,
  LineSeries as LineSeriesDef,
  type AreaData,
  type BarData,
  type BaselineData,
  type BusinessDay,
  type CandlestickData,
  type ChartOptions,
  type DeepPartial,
  type HistogramData,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type LineData,
  type MouseEventParams,
  type SeriesDefinition,
  type SeriesMarker,
  type SeriesOptionsCommon,
  type SeriesType,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";
import { cn } from "../lib/cn";
import { readChartTheme, type ChartThemeColors } from "./chart-theme";

export type { ChartThemeColors } from "./chart-theme";
export type {
  AreaData,
  BarData,
  BaselineData,
  BusinessDay,
  CandlestickData,
  ChartOptions,
  DeepPartial,
  HistogramData,
  IChartApi,
  IPriceLine,
  ISeriesApi,
  LineData,
  MouseEventParams,
  SeriesMarker,
  SeriesOptionsCommon,
  Time,
  UTCTimestamp,
} from "lightweight-charts";

type SeriesDefinitionAny = SeriesDefinition<SeriesType>;
type ChartSeriesOptions = DeepPartial<SeriesOptionsCommon> &
  Record<string, unknown>;

/* -------------------------------------------------------------------------- */
/* Context                                                                    */
/* -------------------------------------------------------------------------- */

interface PriceChartContextValue {
  getChart: () => IChartApi | null;
  getContainer: () => HTMLElement | null;
  getThemeTick: () => number;
  subscribeTheme: (cb: () => void) => () => void;
}

const PriceChartContext = React.createContext<PriceChartContextValue | null>(
  null,
);

function usePriceChartContext(): PriceChartContextValue {
  const ctx = React.useContext(PriceChartContext);
  if (!ctx) {
    throw new Error(
      "Chart series components must be rendered inside a <PriceChart>.",
    );
  }
  return ctx;
}

/** Imperative access to the underlying lightweight-charts API. */
export function usePriceChart(): IChartApi | null {
  const ctx = React.useContext(PriceChartContext);
  const [chart, setChart] = React.useState<IChartApi | null>(
    () => ctx?.getChart() ?? null,
  );
  React.useEffect(() => {
    const tick = () => setChart(ctx?.getChart() ?? null);
    tick();
    return ctx?.subscribeTheme(tick);
  }, [ctx]);
  return chart;
}

/* -------------------------------------------------------------------------- */
/* PriceChart                                                                 */
/* -------------------------------------------------------------------------- */

export interface PriceChartProps {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Raw lightweight-charts options, merged over the ito theme defaults. */
  options?: DeepPartial<ChartOptions>;
  /** Stretch factors for each pane (indexes match `paneIndex` props). */
  paneStretchFactors?: number[];
  /** Text watermark rendered behind the price series. */
  watermark?: { text: string; color?: string; fontSize?: number };
  /** Crosshair subscription. */
  onCrosshairMove?: (param: MouseEventParams<Time>) => void;
  /** Ref exposing the live chart API (set after the chart exists). */
  chartApiRef?: React.RefObject<IChartApi | null>;
}

/**
 * The ito price chart. Renders a responsive lightweight-charts instance
 * themed entirely from design tokens; compose series as children.
 */
export function PriceChart({
  children,
  className,
  style,
  options,
  paneStretchFactors,
  watermark,
  onCrosshairMove,
  chartApiRef,
}: PriceChartProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<IChartApi | null>(null);
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
  const themeObserverRef = React.useRef<MutationObserver | null>(null);
  const watermarkPluginRef = React.useRef<{
    applyOptions: (o: Record<string, unknown>) => void;
  } | null>(null);
  const themeListenersRef = React.useRef(new Set<() => void>());
  const themeTickRef = React.useRef(0);
  const optionsRef = React.useRef(options);
  optionsRef.current = options;
  const watermarkRef = React.useRef(watermark);
  watermarkRef.current = watermark;
  const crosshairRef = React.useRef(onCrosshairMove);
  crosshairRef.current = onCrosshairMove;

  const [, forceRender] = React.useReducer((n: number) => n + 1, 0);

  // The chart is created in the container ref callback so it exists before
  // any child effect runs (refs attach before effects on mount), which lets
  // series children add themselves in plain useEffect.
  const attachContainer = React.useCallback((el: HTMLDivElement | null) => {
    const prev = containerRef.current;
    containerRef.current = el;
    if (el && el !== prev) {
      const colors = readChartTheme();
      // We size the chart manually (autoSize's ResizeObserver fires a frame
      // late, so the very first draw can happen with uninitialized pane
      // geometry — which makes baseline/area gradient fills throw NaN
      // offsets). Reading the live container size and applying it
      // synchronously guarantees a well-formed first frame.
      const chart = createChart(el, {
        layout: {
          background: { color: "transparent" },
          textColor: colors.text,
          fontFamily:
            "var(--font-mono), 'IBM Plex Mono', Menlo, Consolas, monospace",
          attributionLogo: false,
          panes: { separatorColor: colors.border, enableResize: true },
        },
        grid: {
          vertLines: { color: colors.grid },
          horzLines: { color: colors.grid },
        },
        crosshair: {
          vertLine: {
            color: colors.crosshair,
            labelBackgroundColor: colors.crosshair,
          },
          horzLine: {
            color: colors.crosshair,
            labelBackgroundColor: colors.crosshair,
          },
        },
        rightPriceScale: { borderColor: colors.border },
        leftPriceScale: { borderColor: colors.border },
        timeScale: { borderColor: colors.border },
        ...(optionsRef.current ?? {}),
      });
      chartRef.current = chart;
      if (chartApiRef) chartApiRef.current = chart;

      // Manual sizing: apply the current container size immediately, then
      // keep it in sync with a ResizeObserver.
      const resize = () => {
        const target = chartRef.current;
        const host = containerRef.current;
        if (!target || !host || host.clientWidth === 0) return;
        target.applyOptions({
          width: host.clientWidth,
          height: host.clientHeight,
        });
      };
      resize();
      const resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(el);
      resizeObserverRef.current = resizeObserver;

      chart.subscribeCrosshairMove((param) => {
        crosshairRef.current?.(param);
      });

      // Observe `data-theme` flips and re-apply token colors.
      const observer = new MutationObserver(() => {
        const target = chartRef.current;
        if (!target) return;
        const next = readChartTheme();
        target.applyOptions({
          layout: { textColor: next.text },
          grid: {
            vertLines: { color: next.grid },
            horzLines: { color: next.grid },
          },
          crosshair: {
            vertLine: {
              color: next.crosshair,
              labelBackgroundColor: next.crosshair,
            },
            horzLine: {
              color: next.crosshair,
              labelBackgroundColor: next.crosshair,
            },
          },
          rightPriceScale: { borderColor: next.border },
          leftPriceScale: { borderColor: next.border },
          timeScale: { borderColor: next.border },
          ...(optionsRef.current ?? {}),
        });
        watermarkPluginRef.current?.applyOptions({
          text: watermarkRef.current?.text ?? "",
          color: watermarkRef.current?.color ?? next.mutedForeground,
        });
        themeTickRef.current += 1;
        themeListenersRef.current.forEach((cb) => cb());
      });
      observer.observe(document.documentElement, {
        attributes: true,
        // ThemeProvider can drive the theme via `data-theme` or, when
        // configured with `attribute="class"`, the `theme-<name>` class list.
        attributeFilter: ["data-theme", "class"],
      });
      themeObserverRef.current = observer;
      forceRender();
    }
    if (!el && prev) {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      themeObserverRef.current?.disconnect();
      themeObserverRef.current = null;
      chartRef.current?.remove();
      chartRef.current = null;
      if (chartApiRef) chartApiRef.current = null;
      watermarkPluginRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // User-provided options follow-through.
  React.useEffect(() => {
    chartRef.current?.applyOptions(options ?? {});
  }, [options]);

  // Watermark lifecycle.
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (!watermark) {
      // Clear the rendered watermark before dropping the plugin handle;
      // nulling it alone leaves the last text painted on the chart.
      watermarkPluginRef.current?.applyOptions({ lines: [] });
      watermarkPluginRef.current = null;
      return;
    }
    const colors = readChartTheme();
    if (!watermarkPluginRef.current) {
      watermarkPluginRef.current = createTextWatermark(chart.panes()[0], {
        horzAlign: "center",
        vertAlign: "center",
        lines: [
          {
            text: watermark.text,
            color: watermark.color ?? colors.mutedForeground,
            fontSize: watermark.fontSize ?? 48,
            fontFamily:
              "var(--font-display), 'IBM Plex Mono', Menlo, Consolas, monospace",
          },
        ],
      }) as unknown as { applyOptions: (o: Record<string, unknown>) => void };
    } else {
      watermarkPluginRef.current.applyOptions({
        lines: [
          {
            text: watermark.text,
            color: watermark.color ?? colors.mutedForeground,
            fontSize: watermark.fontSize ?? 48,
          },
        ],
      });
    }
  }, [watermark]);

  // Pane stretch factors.
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !paneStretchFactors) return;
    chart.panes().forEach((pane, i: number) => {
      const factor = paneStretchFactors[i];
      if (factor !== undefined) pane.setStretchFactor(factor);
    });
  }, [paneStretchFactors, children]);

  const contextValue = React.useRef<PriceChartContextValue>({
    getChart: () => chartRef.current,
    getContainer: () => containerRef.current,
    getThemeTick: () => themeTickRef.current,
    subscribeTheme: (cb) => {
      themeListenersRef.current.add(cb);
      return () => {
        themeListenersRef.current.delete(cb);
      };
    },
  }).current;

  return (
    <PriceChartContext.Provider value={contextValue}>
      <div
        ref={attachContainer}
        className={cn("relative min-h-[200px] min-w-0", className)}
        style={style}
      >
        {/* lightweight-charts owns this node; children may overlay (tooltips). */}
        {children}
      </div>
    </PriceChartContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/* Series context (for <PriceLine>)                                           */
/* -------------------------------------------------------------------------- */

const SeriesContext = React.createContext<ISeriesApi<SeriesType> | null>(null);

/* -------------------------------------------------------------------------- */
/* Series components                                                          */
/* -------------------------------------------------------------------------- */

export interface SeriesPropsBase {
  /** Row data; set per change (full replace, lightweight-charts style). */
  data: unknown[];
  /** Per-series options merged over the ito defaults. */
  options?: ChartSeriesOptions;
  /** Vertical pane index (creates the pane on first use). */
  paneIndex?: number;
  /** Series markers (buy/sell arrows etc.). */
  markers?: SeriesMarker<Time>[];
  children?: React.ReactNode;
}

const markerPlugins = new WeakMap<
  ISeriesApi<SeriesType>,
  ISeriesMarkersPluginApi<Time>
>();

function markersPluginFor(
  series: ISeriesApi<SeriesType>,
): ISeriesMarkersPluginApi<Time> {
  let plugin = markerPlugins.get(series);
  if (!plugin) {
    plugin = createSeriesMarkers(series, []);
    markerPlugins.set(series, plugin);
  }
  return plugin;
}

function defaultSeriesOptions(
  definition: SeriesDefinitionAny,
  colors: ChartThemeColors,
): ChartSeriesOptions {
  switch (definition.type) {
    case "Candlestick":
      return {
        upColor: colors.up,
        downColor: colors.down,
        borderUpColor: colors.up,
        borderDownColor: colors.down,
        wickUpColor: colors.up,
        wickDownColor: colors.down,
      };
    case "Bar":
      return { upColor: colors.up, downColor: colors.down };
    case "Line":
      return { color: colors.series[0], lineWidth: 2 };
    case "Area":
      return {
        lineColor: colors.up,
        topColor: colors.upArea,
        bottomColor: "transparent",
        lineWidth: 2,
      };
    case "Baseline":
      return {
        baseValue: { type: "price", price: 0 },
        topLineColor: colors.up,
        topFillColor1: colors.upArea,
        topFillColor2: "transparent",
        bottomLineColor: colors.down,
        bottomFillColor1: "transparent",
        bottomFillColor2: colors.downArea,
      };
    case "Histogram":
      return { color: colors.series[5] };
    default:
      return {};
  }
}

function useSeriesLifecycle(
  definition: SeriesDefinitionAny,
  { data, options, paneIndex = 0, markers }: SeriesPropsBase,
): ISeriesApi<SeriesType> | null {
  const ctx = usePriceChartContext();
  const themeTick = ctx.getThemeTick();
  const [series, setSeries] = React.useState<ISeriesApi<SeriesType> | null>(
    null,
  );

  // Create / destroy the series. Recreates when the shape (pane/options
  // payload) changes — never on data changes.
  const optionsKey = JSON.stringify(options ?? {});
  React.useEffect(() => {
    const chart = ctx.getChart();
    if (!chart) return;
    const colors = readChartTheme();
    const s = chart.addSeries(
      definition,
      {
        ...defaultSeriesOptions(definition, colors),
        ...(JSON.parse(optionsKey) as ChartSeriesOptions),
      } as never,
      paneIndex,
    );
    setSeries(s);
    return () => {
      setSeries(null);
      // The chart's container ref cleanup (chart.remove()) runs during the
      // commit phase — BEFORE this passive effect cleanup — so on unmount the
      // chart may already be disposed. Removing a series from a disposed
      // chart throws ("Value is undefined"), which took the whole route down
      // through the error boundary. The chart is going away anyway; drop it.
      try {
        chart.removeSeries(s);
      } catch {
        /* chart already disposed */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, definition, optionsKey, paneIndex]);

  // Theme re-application (without recreating the series).
  React.useEffect(() => {
    if (!series) return;
    const applyTheme = () => {
      series.applyOptions(
        defaultSeriesOptions(definition, readChartTheme()) as never,
      );
    };
    applyTheme();
    return ctx.subscribeTheme(applyTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, definition, ctx]);

  // Data.
  React.useEffect(() => {
    if (!series) return;
    series.setData(data as never);
  }, [series, data]);

  // Markers.
  React.useEffect(() => {
    if (!series || !markers) return;
    markersPluginFor(series).setMarkers(markers);
  }, [series, markers]);

  // Keep themeTick referenced so theme flips re-run marker colors too.
  void themeTick;

  return series;
}

/** Candlestick price series. */
export function CandlestickSeries(props: SeriesPropsBase): React.ReactElement {
  const series = useSeriesLifecycle(CandlestickSeriesDef, props);
  return (
    <SeriesContext.Provider value={series}>
      {props.children}
    </SeriesContext.Provider>
  );
}

/** OHLC bar series. */
export function BarSeries(props: SeriesPropsBase): React.ReactElement {
  const series = useSeriesLifecycle(BarSeriesDef, props);
  return (
    <SeriesContext.Provider value={series}>
      {props.children}
    </SeriesContext.Provider>
  );
}

/** Line series. */
export function LineSeries(props: SeriesPropsBase): React.ReactElement {
  const series = useSeriesLifecycle(LineSeriesDef, props);
  return (
    <SeriesContext.Provider value={series}>
      {props.children}
    </SeriesContext.Provider>
  );
}

/** Area series. */
export function AreaSeries(props: SeriesPropsBase): React.ReactElement {
  const series = useSeriesLifecycle(AreaSeriesDef, props);
  return (
    <SeriesContext.Provider value={series}>
      {props.children}
    </SeriesContext.Provider>
  );
}

/** Baseline series. */
export function BaselineSeries(props: SeriesPropsBase): React.ReactElement {
  const series = useSeriesLifecycle(BaselineSeriesDef, props);
  return (
    <SeriesContext.Provider value={series}>
      {props.children}
    </SeriesContext.Provider>
  );
}

/** Histogram series (volume, P&L distributions). */
export function HistogramSeries(props: SeriesPropsBase): React.ReactElement {
  const series = useSeriesLifecycle(HistogramSeriesDef, props);
  return (
    <SeriesContext.Provider value={series}>
      {props.children}
    </SeriesContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/* PriceLine                                                                  */
/* -------------------------------------------------------------------------- */

export interface PriceLineProps {
  price: number;
  title?: string;
  color?: string;
  lineWidth?: 1 | 2 | 3 | 4;
  lineStyle?: 0 | 1 | 2 | 3 | 4;
  axisLabelVisible?: boolean;
  lineVisible?: boolean;
}

/** A horizontal price level attached to the parent series. */
export function PriceLine({
  price,
  title,
  color,
  lineWidth = 1,
  lineStyle = 2,
  axisLabelVisible = true,
  lineVisible = true,
}: PriceLineProps) {
  const series = React.useContext(SeriesContext);
  const ctx = React.useContext(PriceChartContext);
  const themeTick = ctx?.getThemeTick();
  const lineRef = React.useRef<IPriceLine | null>(null);

  React.useEffect(() => {
    if (!series) return;
    const line = series.createPriceLine({
      price,
      title: title ?? "",
      color: color ?? readChartTheme().mutedForeground,
      lineWidth,
      lineStyle,
      axisLabelVisible,
      lineVisible,
    });
    lineRef.current = line;
    return () => {
      lineRef.current = null;
      try {
        series.removePriceLine(line);
      } catch {
        /* series already removed */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    series,
    price,
    title,
    color,
    lineWidth,
    lineStyle,
    axisLabelVisible,
    lineVisible,
  ]);

  React.useEffect(() => {
    if (!series || !lineRef.current) return;
    lineRef.current.applyOptions({
      color: color ?? readChartTheme().mutedForeground,
    });
  }, [series, color, themeTick]);

  return null;
}
